// ===== WebDAV 客户端（浏览器端直连）=====
// 用于飞牛 NAS（fnOS）等支持 WebDAV 的服务自动备份。
// 能力：连接测试（PROPFIND + MKCOL）、上传（PUT）、列表（PROPFIND Depth:1）、在线探测（OPTIONS）。
// 注意：
//   1) 跨域请求需服务器返回 CORS 头（飞牛 WebDAV 默认不带，若失败按提示配置）
//   2) 公网 HTTPS 页面无法访问局域网 HTTP（浏览器混合内容拦截），需 WebDAV 走 HTTPS 或内网穿透

export interface WebDavConfig {
  url: string; // http://192.168.1.10:5005 或 https://nas.example.com:5006
  user: string;
  pass: string;
  dir: string; // 备份目录名，默认「昌发工作台备份」
  autoSync: boolean; // 打开工作台时自动探测并同步
}

export interface WebDavFile {
  name: string;
  size: number;
  modified: string;
}

const DEFAULT_TIMEOUT_MS = 8000;

/** Basic Auth 头（中文账号/密码需 UTF-8 编码后 base64） */
export function baseAuthHeader(user: string, pass: string): string {
  const bytes = new TextEncoder().encode(`${user}:${pass}`);
  let bin = '';
  bytes.forEach((b) => {
    bin += String.fromCharCode(b);
  });
  return 'Basic ' + btoa(bin);
}

interface FetchOut {
  status: number;
  text: string;
}

async function webDavFetch(
  url: string,
  opts: { method: string; headers?: Record<string, string>; body?: string },
  timeoutMs = DEFAULT_TIMEOUT_MS
): Promise<FetchOut> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const res = await fetch(url, {
      method: opts.method,
      headers: opts.headers,
      body: opts.body,
      signal: ctrl.signal,
    });
    const text = await res.text();
    return { status: res.status, text };
  } finally {
    clearTimeout(timer);
  }
}

export interface WebDavTestResult {
  ok: boolean;
  message: string;
  status?: number;
}

/** 测试连接：探测服务器 → 校验账号 → 确保备份目录存在（不存在则创建） */
export async function testWebDavConnection(cfg: WebDavConfig): Promise<WebDavTestResult> {
  const base = cfg.url.replace(/\/+$/, '');
  const auth = { Authorization: baseAuthHeader(cfg.user, cfg.pass) };
  try {
    // 1) 探测服务器 + 认证校验
    const probe = await webDavFetch(`${base}/`, {
      method: 'PROPFIND',
      headers: { ...auth, Depth: '0', 'Content-Type': 'application/xml' },
    });
    if (probe.status === 401 || probe.status === 403) {
      return { ok: false, message: `认证失败：账号或密码错误（状态 ${probe.status}）`, status: probe.status };
    }
    if (probe.status !== 207 && probe.status !== 200) {
      return { ok: false, message: `服务器响应异常（状态 ${probe.status}），请检查地址与端口`, status: probe.status };
    }

    // 2) 检查 / 创建备份目录
    const dirPath = `${base}/${encodeURIComponent(cfg.dir)}`;
    const dir = await webDavFetch(dirPath, { method: 'PROPFIND', headers: { ...auth, Depth: '0' } });
    if (dir.status === 404 || dir.status === 409) {
      const mk = await webDavFetch(dirPath, { method: 'MKCOL', headers: auth });
      if (mk.status !== 201 && mk.status !== 200 && mk.status !== 405) {
        return {
          ok: false,
          message: `备份目录「${cfg.dir}」不存在且创建失败（状态 ${mk.status}），请检查账号是否有写权限`,
          status: mk.status,
        };
      }
    } else if (dir.status !== 207 && dir.status !== 200) {
      return { ok: false, message: `访问备份目录失败（状态 ${dir.status}）`, status: dir.status };
    }

    return { ok: true, message: `连接成功：${base}` };
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') {
      return { ok: false, message: '连接超时（8 秒无响应），请检查 NAS 是否开机、地址是否正确' };
    }
    const msg = e instanceof Error ? e.message : '未知错误';
    if (msg === 'Failed to fetch') {
      return {
        ok: false,
        message: '连接失败：跨域被浏览器拦截或地址不可达。若本站为「deploy-feiniu 同源部署」（NAS-IP:8112 访问），地址请填 /webdav/共享文件夹名；直连飞牛请核对地址与端口（HTTP 5005）',
      };
    }
    return { ok: false, message: `连接失败：${msg}` };
  }
}

/** 上传备份文件到 WebDAV（PUT 单请求，备份 JSON 通常几 MB 以内） */
export async function webDavUpload(
  cfg: WebDavConfig,
  remoteName: string,
  content: string
): Promise<{ ok: boolean; message: string; status?: number }> {
  const base = cfg.url.replace(/\/+$/, '');
  const path = `${base}/${encodeURIComponent(cfg.dir)}/${encodeURIComponent(remoteName)}`;
  try {
    const res = await webDavFetch(path, {
      method: 'PUT',
      headers: {
        Authorization: baseAuthHeader(cfg.user, cfg.pass),
        'Content-Type': 'application/json',
      },
      body: content,
    });
    if (res.status >= 200 && res.status < 300) return { ok: true, message: `已上传：${remoteName}` };
    return { ok: false, message: `上传失败（状态 ${res.status}）`, status: res.status };
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') {
      return { ok: false, message: '上传超时（8 秒无响应），备份包较大或 NAS 响应慢' };
    }
    return { ok: false, message: `上传失败：${e instanceof Error ? e.message : '未知错误'}` };
  }
}

/** 列出备份目录下的文件（PROPFIND Depth:1） */
export async function webDavList(
  cfg: WebDavConfig
): Promise<{ ok: boolean; files: WebDavFile[]; message?: string }> {
  const base = cfg.url.replace(/\/+$/, '');
  const path = `${base}/${encodeURIComponent(cfg.dir)}`;
  try {
    const res = await webDavFetch(path, {
      method: 'PROPFIND',
      headers: { Authorization: baseAuthHeader(cfg.user, cfg.pass), Depth: '1' },
    });
    if (res.status !== 207 && res.status !== 200) {
      return { ok: false, files: [], message: `列表获取失败（状态 ${res.status}）` };
    }
    const doc = new DOMParser().parseFromString(res.text, 'application/xml');
    const files: WebDavFile[] = [];
    doc.querySelectorAll('response').forEach((r) => {
      const href = r.querySelector('href')?.textContent || '';
      const name = decodeURIComponent(href.split('/').filter(Boolean).pop() || '');
      if (!name || name === cfg.dir) return;
      const size = Number(r.querySelector('getcontentlength')?.textContent || 0);
      const modified = r.querySelector('getlastmodified')?.textContent || '';
      files.push({ name, size, modified });
    });
    return { ok: true, files };
  } catch (e) {
    return { ok: false, files: [], message: `列表获取失败：${e instanceof Error ? e.message : '未知错误'}` };
  }
}

/** 快速探测 NAS 是否在线（用于打开工作台时智能同步；离线静默跳过） */
export async function probeWebDavOnline(cfg: WebDavConfig, timeoutMs = 3000): Promise<boolean> {
  try {
    const base = cfg.url.replace(/\/+$/, '');
    const res = await webDavFetch(`${base}/`, {
      method: 'OPTIONS',
      headers: { Authorization: baseAuthHeader(cfg.user, cfg.pass) },
    }, timeoutMs);
    return res.status < 500;
  } catch {
    return false;
  }
}
